import { useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'

import { listAbTests, setAbTestStatus as setAbTestStatusRequest } from '../../../api/abTests.js'
import { listElements } from '../../../api/elements.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'
import { canTransitionAbTestStatus, getAvailableAbTestStatuses } from '../../../local/abTestStatusStateMachine.js'
import { actions } from '../../../local/remoteConfigStore.js'
import { useRemoteConfigAppState, useRemoteConfigMemo } from '../../../local/useRemoteConfigStore.js'
import { METRIC_DAYS_OPTIONS, TARGET_METRIC_OPTIONS } from './abTestMetricOptions.js'

function sortByNumber(value) {
  return Number.isFinite(value) ? value : 0
}

function getLatestDraftVersionId(appState, groupConfigId) {
  const versions = Object.values(appState?.groupConfigVersions || {}).filter(
    (v) => v && v.groupConfigId === groupConfigId && v.status === 'draft',
  )
  versions.sort((a, b) => String(b?.createdAt || '').localeCompare(String(a?.createdAt || '')))
  return versions[0]?.id || null
}

function getSegmentsForGroupConfig(appState, groupConfigId) {
  const draftVersionId = getLatestDraftVersionId(appState, groupConfigId)
  const segments = Object.values(appState?.groupSegments || {}).filter((gs) => {
    if (!draftVersionId || !gs) return false
    return gs.groupConfigVersionId === draftVersionId
  })
  segments.sort((a, b) => (a?.priority || 0) - (b?.priority || 0))
  return segments
}

export default function AbTestDetailPage() {
  const { applicationId } = useOutletContext()
  const { confirm } = useFeedback()
  const { abTestId } = useParams()
  const appState = useRemoteConfigAppState(applicationId)
  const storeActions = useRemoteConfigMemo(() => actions(), [])

  const abTest = appState?.abTests?.[abTestId] || null
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const [values, setValues] = useState(() => ({
    name: abTest?.name || '',
    description: abTest?.description || '',
    groupConfigId: abTest?.groupConfigId || '',
    groupSegmentId: '',
    audience: abTest?.audience ?? '',
    targetMetric: abTest?.targetMetric || '',
    metricDays: abTest?.metricDays ?? '',
    targetUserCount: abTest?.targetUserCount ?? '',
    isOnlyNewUser: Boolean(abTest?.isOnlyNewUser),
    automaticDistributionStop: Boolean(abTest?.automaticDistributionStop),
    releaseAllVersions: Boolean(abTest?.releaseAllVersions),
    testFinishDate: abTest?.testFinishDate || '',
  }))

  const variants = useMemo(() => {
    if (!abTest) return []
    return Object.values(appState?.abVariants || {})
      .filter((v) => v.abTestId === abTest.id)
      .sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
  }, [appState, abTest])

  const bindingsByVariantId = useMemo(() => {
    const map = {}
    for (const binding of Object.values(appState?.abVariantElementBindings || {})) {
      if (!binding || !binding.abVariantId) continue
      if (!map[binding.abVariantId]) map[binding.abVariantId] = []
      map[binding.abVariantId].push(binding)
    }
    for (const variantId of Object.keys(map)) {
      map[variantId].sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
    }
    return map
  }, [appState])

  const publishedElements = useMemo(() => {
    const list = Object.values(appState?.elements || {}).filter((el) => el?.status === 'published')
    list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    return list
  }, [appState])

  function getElementOptionsForVariant(variantId) {
    const publishedIds = new Set(publishedElements.map((el) => String(el.id)))
    const bound = bindingsByVariantId[variantId] || []
    const extra = bound
      .map((b) => appState?.elements?.[String(b.elementId)] || appState?.elements?.[b.elementId])
      .filter(Boolean)
      .filter((el) => !publishedIds.has(String(el.id)))
    return [...publishedElements, ...extra]
  }

  const [newVariantName, setNewVariantName] = useState('')
  const [newVariantElementId, setNewVariantElementId] = useState('')

  const groupConfigs = useMemo(() => {
    const list = Object.values(appState?.groupConfigs || {}).filter(Boolean)
    list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    return list
  }, [appState])

  const availableSegments = useMemo(() => {
    if (!values.groupConfigId) return []
    return getSegmentsForGroupConfig(appState, values.groupConfigId)
  }, [appState, values.groupConfigId])

  const selectedGroupSegment = useMemo(() => {
    if (!values.groupSegmentId) return null
    return appState?.groupSegments?.[values.groupSegmentId] || null
  }, [appState, values.groupSegmentId])

  const selectedSegmentation = useMemo(() => {
    if (!selectedGroupSegment?.segmentationId) return null
    return appState?.segmentations?.[selectedGroupSegment.segmentationId] || null
  }, [appState, selectedGroupSegment])

  useEffect(() => {
    if (abTest) return
    let cancelled = false
    setIsLoading(true)
    listAbTests(applicationId)
      .then((items) => {
        if (cancelled) return
        storeActions.hydrateAbTests(applicationId, items)
      })
      .catch((loadError) => {
        if (cancelled) return
        setError(loadError)
      })
      .finally(() => {
        if (cancelled) return
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [abTest, applicationId, storeActions])

  useEffect(() => {
    if (!abTest) return
    const segments = getSegmentsForGroupConfig(appState, abTest.groupConfigId)
    const selectedByKey = segments.find(
      (item) =>
        String(item.segmentKey || '').trim() === String(abTest.segmentKey || '').trim() ||
        String(item.segmentationId || '').trim() === String(abTest.segmentKey || '').trim(),
    )
    setValues({
      name: abTest.name || '',
      description: abTest.description || '',
      groupConfigId: abTest.groupConfigId || '',
      groupSegmentId: selectedByKey?.id || '',
      audience: abTest.audience ?? '',
      targetMetric: abTest.targetMetric || '',
      metricDays: abTest.metricDays ?? '',
      targetUserCount: abTest.targetUserCount ?? '',
      isOnlyNewUser: Boolean(abTest.isOnlyNewUser),
      automaticDistributionStop: Boolean(abTest.automaticDistributionStop),
      releaseAllVersions: Boolean(abTest.releaseAllVersions),
      testFinishDate: abTest.testFinishDate || '',
    })
  }, [abTest?.id, appState])

  useEffect(() => {
    let cancelled = false
    listElements(applicationId)
      .then((items) => {
        if (cancelled) return
        storeActions.hydrateElements(applicationId, items)
      })
      .catch((loadError) => {
        if (cancelled) return
        setError(loadError)
      })
    return () => {
      cancelled = true
    }
  }, [applicationId, storeActions])

  const totalTraffic = useMemo(
    () => variants.reduce((sum, v) => sum + (Number(v.trafficPercent) || 0), 0),
    [variants],
  )

  if (!abTest) {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">{isLoading ? 'Loading AB test...' : 'AB test not found'}</div>
        <div className="sectionActions">
          <Link to={`/applications/${applicationId}/ab-tests`} className="button buttonGhost">
            Back to AB tests
          </Link>
        </div>
      </section>
    )
  }

  function onChange(field) {
    return (e) => {
      const isCheckbox = e.target.type === 'checkbox'
      const nextValue = isCheckbox ? e.target.checked : e.target.value
      setValues((prev) => {
        if (field === 'groupConfigId') {
          return { ...prev, groupConfigId: nextValue, groupSegmentId: '' }
        }
        if (field === 'automaticDistributionStop' && !nextValue) {
          return { ...prev, automaticDistributionStop: nextValue, testFinishDate: '' }
        }
        return { ...prev, [field]: nextValue }
      })
    }
  }

  function handleSave() {
    setError(null)
    const name = String(values.name || '').trim()
    if (!name) {
      setError(new Error('Name is required'))
      return
    }
    if (!values.groupConfigId) {
      setError(new Error('Group config is required'))
      return
    }
    if (!values.groupSegmentId || !selectedGroupSegment?.id) {
      setError(new Error('Segment is required'))
      return
    }
    const audience = values.audience === '' ? null : Number(values.audience)
    const metricDays = values.metricDays === '' ? null : Number(values.metricDays)
    const targetUserCount = values.targetUserCount === '' ? null : Number(values.targetUserCount)
    if (audience !== null && (!Number.isInteger(audience) || audience < 0 || audience > 100)) {
      setError(new Error('Audience must be an integer between 0 and 100'))
      return
    }
    if (metricDays !== null && (!Number.isInteger(metricDays) || metricDays < 0)) {
      setError(new Error('Metric days must be a non-negative integer'))
      return
    }
    if (targetUserCount !== null && (!Number.isInteger(targetUserCount) || targetUserCount < 0)) {
      setError(new Error('Target user count must be a non-negative integer'))
      return
    }
    if (metricDays !== null && !String(values.targetMetric || '').trim()) {
      setError(new Error('Target metric is required when metric days is set'))
      return
    }
    if (String(values.targetMetric || '').trim() && metricDays === null) {
      setError(new Error('Metric days is required when target metric is set'))
      return
    }
    if (values.automaticDistributionStop && !values.testFinishDate) {
      setError(new Error('Test finish date is required when automatic stop is enabled'))
      return
    }
    storeActions.updateAbTest(applicationId, abTest.id, {
      name,
      description: values.description,
      groupConfigId: values.groupConfigId,
      groupSegmentId: values.groupSegmentId,
      segmentationId: selectedGroupSegment?.segmentationId ?? abTest.segmentationId,
      audience,
      targetMetric: values.targetMetric,
      metricDays,
      targetUserCount,
      isOnlyNewUser: values.isOnlyNewUser,
      automaticDistributionStop: values.automaticDistributionStop,
      releaseAllVersions: values.releaseAllVersions,
      testFinishDate: values.testFinishDate ? new Date(values.testFinishDate).toISOString() : null,
    })
  }

  async function handleStatusChange(status) {
    if (!canTransitionAbTestStatus(abTest.status, status)) {
      setError(new Error(`Status transition is not allowed: ${abTest.status} -> ${status}`))
      return
    }
    try {
      const updated = await setAbTestStatusRequest(applicationId, abTest.id, status)
      if (updated) {
        storeActions.hydrateAbTests(applicationId, [updated])
      }
    } catch (e) {
      setError(e)
    }
  }

  function handleAddVariant() {
    setError(null)
    if (!String(newVariantName || '').trim()) {
      setError(new Error('Variant name is required'))
      return
    }
    if (!newVariantElementId) {
      setError(new Error('Element is required for a new variant'))
      return
    }
    try {
      const variantId = storeActions.addAbVariant(applicationId, abTest.id, newVariantName)
      if (variantId) {
        storeActions.addAbVariantElement(applicationId, variantId, newVariantElementId, {})
      }
      setNewVariantName('')
      setNewVariantElementId('')
    } catch (e) {
      setError(e)
    }
  }

  async function handleRemoveVariant(variantId) {
    setError(null)
    const confirmed = await confirm({
      title: 'Delete variant',
      message: 'Delete this variant from the test?',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      storeActions.removeAbVariant(applicationId, variantId)
    } catch (e) {
      setError(e)
    }
  }

  function handleVariantElementChange(variantId, elementId) {
    setError(null)
    const variantBindings = (bindingsByVariantId[variantId] || []).filter(Boolean)
    try {
      for (const binding of variantBindings) {
        storeActions.removeAbVariantElement(applicationId, binding.id)
      }
      if (elementId) {
        storeActions.addAbVariantElement(applicationId, variantId, elementId, {})
      }
    } catch (e) {
      setError(e)
    }
  }

  const availableStatuses = getAvailableAbTestStatuses(abTest)

  return (
    <div className="page">
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">AB test: {abTest.name || 'Untitled'}</h1>
        </div>
        <div className="buttonGroup">
          <Link to={`/applications/${applicationId}/ab-tests`} className="button buttonGhost">
            Back
          </Link>
          <button type="button" className="button buttonPrimary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>

      {error ? <div className="formError">{String(error?.message || error)}</div> : null}

      <section className="card formCard">
        <div className="cardTitle">Settings</div>
        <div className="formGrid">
          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} onChange={onChange('name')} />
          </label>
          <label className="formField">
            <span className="formLabel">Status</span>
            <select className="textInput" value={abTest.status} onChange={(e) => handleStatusChange(e.target.value)}>
              {availableStatuses.map((statusValue) => (
                <option key={statusValue} value={statusValue}>
                  {statusValue}
                </option>
              ))}
            </select>
          </label>
          <label className="formField">
            <span className="formLabel">Description</span>
            <input className="textInput" value={values.description} onChange={onChange('description')} />
          </label>
          <label className="formField">
            <span className="formLabel">Audience (%)</span>
            <input
              className="textInput"
              type="number"
              min="0"
              max="100"
              value={values.audience}
              onChange={onChange('audience')}
              placeholder="0-100"
            />
          </label>
          <label className="formField">
            <span className="formLabel">Target metric</span>
            <select className="textInput" value={values.targetMetric} onChange={onChange('targetMetric')}>
              <option value="">Select target metric…</option>
              {TARGET_METRIC_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
          <label className="formField">
            <span className="formLabel">Metric/Day:</span>
            <select
              className="textInput"
              value={values.metricDays === undefined || values.metricDays === null ? '' : String(values.metricDays)}
              onChange={onChange('metricDays')}
            >
              <option value="">Select…</option>
              {METRIC_DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label className="formField">
            <span className="formLabel">Target user count</span>
            <input
              className="textInput"
              type="number"
              min="0"
              value={values.targetUserCount}
              onChange={onChange('targetUserCount')}
              placeholder="e.g. 10000"
            />
          </label>
          <label className="formField">
            <span className="formLabel">Group config</span>
            <select className="textInput" value={values.groupConfigId} onChange={onChange('groupConfigId')}>
              <option value="">Select group config…</option>
              {groupConfigs.map((gc) => (
                <option key={gc.id} value={gc.id}>
                  {gc.name || gc.id}
                </option>
              ))}
            </select>
          </label>
          <label className="formField">
            <span className="formLabel">Segment (from latest draft)</span>
            <select className="textInput" value={values.groupSegmentId} onChange={onChange('groupSegmentId')}>
              <option value="">Select segment…</option>
              {availableSegments.map((gs) => {
                const seg = appState?.segmentations?.[gs.segmentationId]
                return (
                  <option key={gs.id} value={gs.id}>
                    {gs.priority}. {seg?.name || 'Unnamed'} {gs.isDefault ? '(default)' : ''}
                  </option>
                )
              })}
            </select>
          </label>
          <label className="formField formSwitch">
            <span className="formLabel formLabelNoMargin">
              Test only on new users
            </span>
            <input
              type="checkbox"
              className="formCheckInput"
              checked={values.isOnlyNewUser}
              onChange={onChange('isOnlyNewUser')}
            />
          </label>
          <label className="formField formSwitch">
            <span className="formLabel formLabelNoMargin">
              Automatic distribution stop
            </span>
            <input
              type="checkbox"
              className="formCheckInput"
              checked={values.automaticDistributionStop}
              onChange={onChange('automaticDistributionStop')}
            />
          </label>
          {values.automaticDistributionStop ? (
            <label className="formField">
              <span className="formLabel">Test finish date (UTC)</span>
              <input
                className="textInput"
                type="datetime-local"
                value={values.testFinishDate}
                onChange={onChange('testFinishDate')}
              />
            </label>
          ) : null}
          <label className="formField formSwitch">
            <span className="formLabel formLabelNoMargin">
              Release all versions
            </span>
            <input
              type="checkbox"
              className="formCheckInput"
              checked={values.releaseAllVersions}
              onChange={onChange('releaseAllVersions')}
            />
          </label>
        </div>
      </section>

      <section className="card cardSectionTop">
        <div className="cardTitle">Variants</div>

        {variants.length === 0 ? (
          <p className="muted inlineMessage">
            No variants.
          </p>
        ) : (
          <table className="table" aria-label="AB variants">
            <thead>
              <tr>
                <th>Order</th>
                <th>Name</th>
                <th>Traffic %</th>
                <th>Elements</th>
                <th className="alignRight" />
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => {
                const variantBindings = bindingsByVariantId[variant.id] || []
                const primaryBinding = variantBindings[0] || null
                const pct = Number(variant.trafficPercent) || 0
                const shareOfTotal =
                  totalTraffic > 0 ? Math.round((pct / totalTraffic) * 100) : 0
                return (
                  <tr key={variant.id}>
                    <td className="muted">{variant.order}</td>
                    <td>{variant.name}</td>
                    <td className="abVariantsTrafficCell">
                      <input
                        className="textInput"
                        inputMode="numeric"
                        value={String(variant.trafficPercent ?? 0)}
                        onChange={(e) =>
                          storeActions.setAbVariantTraffic(applicationId, variant.id, e.target.value)
                        }
                      />
                      {totalTraffic > 0 ? (
                        <span className="muted mutedXs abVariantsShareHint">
                          ({shareOfTotal}% of total)
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <div className="builderInlineRow">
                        <select
                          className="textInput"
                          value={String(primaryBinding?.elementId || '')}
                          onChange={(e) => handleVariantElementChange(variant.id, e.target.value)}
                        >
                          <option value="">Select element…</option>
                          {getElementOptionsForVariant(variant.id).map((el) => (
                            <option key={el.id} value={String(el.id)}>
                              {el.name || el.id} ({el.elementType}, v{el.version})
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="alignRight">
                      <span className="tableActions">
                        <button
                          type="button"
                          className="button buttonGhost buttonDanger"
                          onClick={() => handleRemoveVariant(variant.id)}
                          disabled
                        >
                          Delete
                        </button>
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        <div className="builderInlineRow builderInlineRowMt8">
          <input
            className="textInput"
            value={newVariantName}
            onChange={(e) => setNewVariantName(e.target.value)}
            placeholder="Variant name (e.g. C)"
          />
          <select
            className="textInput"
            value={String(newVariantElementId || '')}
            onChange={(e) => setNewVariantElementId(e.target.value)}
          >
            <option value="">Select element…</option>
            {publishedElements.map((el) => (
              <option key={el.id} value={String(el.id)}>
                {el.name || el.id} ({el.elementType}, v{el.version})
              </option>
            ))}
          </select>
          <button type="button" className="button buttonPrimary" onClick={handleAddVariant}>
            Add
          </button>
        </div>
      </section>
    </div>
  )
}

